<template>
	<div class="stretch-layout">
		<data-loader :url="props.url"
					 on-scroll
					 :flat="props.flat"
					 ref="loaderElement"
					 :auth="$auth.loggedIn"
					 :scroll-load-margin="500"
					 @updated="handleUpdatedFeed"
					 :params="activityParams"
					 v-model:data="activities">

			<template v-if="!withoutCreate" #alwaysshow>
				<div v-if="$auth.user" :class="{'border-b': props.flat}">
					<template v-if="props.flat">
						<lazy-activity-form :model="props.model ?? $auth.user" :color="props.model?.color" :initial-creator="initialCreator" :only-auth-creator="onlyAuthCreator"
											@created="handleCreatedActivity"/>
						<slot name="below_form"/>
					</template>
					<lazy-card v-else xs-tile sm-tile>
						<lazy-activity-form :model="props.model ?? $auth.user" :color="props.model?.color" :initial-creator="initialCreator" :only-auth-creator="onlyAuthCreator"
											@created="handleCreatedActivity"/>
						<slot name="below_form"/>
					</lazy-card>
				</div>
				<slot name="above_feed"/>
			</template>

			<template #loading>
				<div :class="{'divided': props.flat}" v-for="activityLoader in 4" :key="'activity-loader-' + activityLoader">
					<activity-skeleton xs-tile sm-tile :flat="props.flat"/>
				</div>
			</template>

			<template v-slot="{data}">
				<template v-for="(activity, activityIndex) in data" :key="activity.id">
					<div :class="{'divided': props.flat}">
						<activity :activity="activity"
								  :feed-owner="props.model"
								  @show-likes="showLikes(activity)"
								  @show-comments="showComments(activity)"
								  @destroyed="handleDestroyedActivity(activity)"
								  @edit="editActivity(activity)" xs-tile sm-tile :flat="props.flat"/>
					</div>

					<div v-if="props.withAds && (activityIndex - 1) % 5 === 0" :class="{'divided': props.flat}" class="p-16 lg:px-24">
						<awin-ad-card :query-id="awinAdQueryIds[Math.floor(Math.random()*awinAdQueryIds.length)]" class="rounded border"/>
					</div>

				</template>

			</template>

			<template #missing>
				<slot name="missing"/>
				<lazy-missing-info v-if="!$slots.missing" xs-tile sm-tile card :flat="props.flat" :no-spacing="props.flat" class="min-h-200"
								   :text="missingText ?? (model.model_type === ModelType.User && model.id === $auth.id ? $t('missing.myself.activity') : $t(`missing.community_posts`, {name: model.greeting_name}))"/>
			</template>
		</data-loader>

		<lazy-likes-modal v-if="selectedActivity" v-model="showLikerModal" :count="selectedActivity.likes?.length ?? 10"
						  :url="`/v1/${selectedActivity.owner.model_name}s/${selectedActivity.owner.id}/activities/${selectedActivity.id}/likes`"
						  @closed="selectedActivity = null"/>

		<lazy-ajax-form-modal v-if="selectedActivity && editData" v-model="showEditModal"
							  :form-options="{action: `/v1/${selectedActivity.owner.model_name}s/${selectedActivity.owner.id}/activities/${selectedActivity.id}`, method: 'PUT', data: editData}"
							  :modal-options="{'show-close-button': false, title: $t('action.edit')}"
							  @success="handleUpdatedActivity">

			<lazy-form-textarea name="text"
								:rows="3"
								:label="$t('label.text')"
								v-model="editData.text"/>

			<lazy-form-input name="url"
							 type="url"
							 clearable
							 placeholder="https://spoferan.com"
							 :label="$t('label.link')"
							 v-model="editData.url"/>

			<template #footer>
				<lazy-form-btn :action="`/v1/${selectedActivity.owner.model_name}s/${selectedActivity.owner.id}/activities/${selectedActivity.id}`" method="DELETE"
							   text
							   color="error"
							   notification="destroyed"
							   :confirm="{title: $t('confirm.activity.destroy.title'), message: $t('confirm.activity.destroy.message')}"
							   @success="handleDestroyedActivity(selectedActivity)" :id="'btn-delete-activity-' + selectedActivity.id">
					{{ $t('action.destroy') }}
				</lazy-form-btn>
			</template>

		</lazy-ajax-form-modal>

		<slide-sheet v-model="showCommentsModal" :title="$t('label.comments')" :width="480">
			<template v-if="selectedActivity" #default>
				<comment-feed :url="`/v1/${selectedActivity.owner.model_name}s/${selectedActivity.owner.id}/activities/${selectedActivity.id}/comments`"
							  v-model:comments-count="commentsCount"
							  v-model:comments="comments"
							  :cache-buster="commentLoaderCacheBuster"
							  @toggle-like="handleLikedComment" class="px-16"/>
			</template>

			<template v-if="selectedActivity" #footer>
				<div v-if="$auth.loggedIn" class="py-16 pr-16 pl-12 border-t">
					<lazy-comment-form :action="`/v1/${selectedActivity.owner.model_name}s/${selectedActivity.owner.id}/activities/${selectedActivity.id}/comments`"
									   :inline="!$device.isDesktop"
									   @success="handleCreatedComment"/>
				</div>
				<lazy-login-to-see-more v-else flat no-spacing :text="$t('action.login_to_comment')"/>
			</template>

		</slide-sheet>

	</div>
</template>

<script setup lang="ts">
import {MainModelPreview, ModelType, scrollToSelector, stripHtml} from "@spoferan/spoferan-ts-core";

const route = useRoute();
const {t} = useI18n();
const config = useRuntimeConfig();
const {$date, $auth} = useNuxtApp();

const props = withDefaults(defineProps<{

	// The API URL from which to load the activities
	url: string,

	// The model to display the feed for
	model?: MainModelPreview,

	// The model to select as the initial creator on the activity form when available
	initialCreator?: MainModelPreview,

	// The additional params to submit when loading the activities to display
	params?: Record<string, any>,

	// Whether a new activity is not allowed to be created on the feed
	withoutCreate?: boolean,

	// The text to display when no activities exist
	missingText?: string,

	// Whether a flat layout shall be applied
	flat?: boolean,

	// Whether to display ads between the activity cards
	withAds?: boolean,

	// Whether the authenticated user can only post as himself
	onlyAuthCreator?: boolean
}>(), {
	params: {}
});

const emit = defineEmits(['created']);

defineExpose({
	reload
});

const activities = ref([]);
const initiallyLoaded = ref(false);
const showLikerModal = ref(false);
const showEditModal = ref(false);
const showCommentsModal = ref(false);
const commentLoaderCacheBuster = ref(null);
const selectedActivity = ref(null);
const editData = ref(null);
const loaderElement = ref(null);

const {comments, commentsCount, handleCreatedComment} = useCommentable();
watch(commentsCount, (newValue) => {
	if (selectedActivity.value) {
		selectedActivity.value.comments_count = newValue;
	}
});

const awinAdQueryIds = [
	's=3295032&v=11266&q=324874&r=555993',
	's=3295044&v=11266&q=324874&r=555993'
]

const activityParams = computed(() => {
	const params = {...props.params}
	if (route.query.analyze) {
		params.analyze = true;
	}
	return params;
});

function handleUpdatedFeed() {
	if (!initiallyLoaded.value) {
		if (route.hash) {
			// Wait until all activity components are rendered
			nextTick(function () {
				setTimeout(() => {
					scrollToSelector(route.hash, -96);
				}, 50); // We need to wait a bit to ensure the scroll works for the right position
			});
		}
	}

	initiallyLoaded.value = true;
}

/**
 * Executes the actions after an activity was successfully created for the profile.
 */
function handleCreatedActivity(activity) {

	// Add default values for non returned props
	activity.comments_count = 0;

	activities.value.splice(0, 0, activity);
	emit('created', activity)
}

/**
 * Executes the actions after an activity was successfully updated for the profile.
 */
function handleUpdatedActivity(data) {
	const activity = activities.value.find(activity => activity.id === parseInt(data.id));
	if (activity) {
		Object.keys(data).forEach(key => {
			// Otherwise we override the club permissions
			if (key !== 'owner' && key !== 'creator') {
				activity[key] = data[key];
			}
		});
	}
	editData.value = null;
}

/**
 * Executes the actions after an activity was successfully deleted for the profile.
 */
function handleDestroyedActivity(activity) {
	// Remove the activity from the list
	activities.value.splice(activities.value.indexOf(activity), 1);
	showEditModal.value = false;
}

function reload(...args) {
	loaderElement.value.reload(...args);
}

function showLikes(activity) {
	selectedActivity.value = activity;
	nextTick(() => {
		showLikerModal.value = true;
	});
}

function showComments(activity) {
	selectedActivity.value = activity;
	commentsCount.value = activity.comments_count;

	// We do not inline the params in the template as it would be reactive and therefore trigger a reload when we call 'updateActivityCacheBuster'
	commentLoaderCacheBuster.value = $date(activity.updated_at).unix();

	nextTick(() => {
		showCommentsModal.value = true;
	});
}

function handleLikedComment() {
	if (!selectedActivity.value) {
		return;
	}

	updateActivityCacheBuster(selectedActivity.value);
}

function updateActivityCacheBuster(activity) {
	activity.updated_at = $date().format();
}

/**
 * Shows the edit modal for the given activity.
 *
 * @param activity
 */
function editActivity(activity) {

	// If the activity was created on resultastic, it can only be edited there due to advanced formatting options
	if (activity.owner.model_type === ModelType.Event) {
		window.open(`${config.public.resultasticUrl}/${activity.owner._key}/news/${activity._key}/edit`, '_blank');
		return;
	}

	selectedActivity.value = activity;
	editData.value = {
		text: stripHtml(activity.text),
		existing_media: activity.media && activity.media.length ? activity.media.map(media => media.id) : null,
		url: activity.url,
	}
	nextTick(function () {
		showEditModal.value = true;
	});
}
</script>